import { useState, useMemo } from 'react';
import { formatDateShort } from "../utilityFunctions";
import { toast } from "react-hot-toast";
import { toastStyle } from "../../Containers/continerUtilityFunctions";

/**
 * Custom hook for managing snapshot data processing
 * @param {Array} selectedSnapshots - Array of snapshot objects
 * @returns {Object} - Processed snapshot data and handlers
 */
export const useSnapshotData = (selectedSnapshots) => {
  const sortedSnapshots = useMemo(() => {
    return [...(selectedSnapshots || [])].sort((a, b) => 
      new Date(a.created_at) - new Date(b.created_at)
    );
  }, [selectedSnapshots]);

  // Initialize state with proper defaults using lazy initial state
  const [selectedBaselineId, setSelectedBaselineId] = useState(() => 
    sortedSnapshots[0]?.id || null
  );
  const [selectedCurrentId, setSelectedCurrentId] = useState(() => 
    sortedSnapshots[sortedSnapshots.length - 1]?.id || null
  );
  const [selectedPenultimateId, setSelectedPenultimateId] = useState(() => 
    sortedSnapshots[sortedSnapshots.length - 2]?.id || null
  );

  // Get snapshot objects
  const snapshotObjects = useMemo(() => {
    const snapshotAObj = sortedSnapshots.find(s => s.id === selectedPenultimateId);
    const snapshotBObj = sortedSnapshots.find(s => s.id === selectedCurrentId);
    const baseSnapshotObj = sortedSnapshots.find(s => s.id === selectedBaselineId);

    return {
      snapshotAObj,
      snapshotBObj,
      baseSnapshotObj,
      snapshotADate: snapshotAObj ? formatDateShort(snapshotAObj.created_at) : "Date Not Found",
      snapshotBDate: snapshotBObj ? formatDateShort(snapshotBObj.created_at) : "Date Not Found",
      baseSnapshotDate: baseSnapshotObj ? formatDateShort(baseSnapshotObj.created_at) : "Date Not Found"
    };
  }, [sortedSnapshots, selectedPenultimateId, selectedCurrentId, selectedBaselineId]);

  return {
    sortedSnapshots,
    selectedBaselineId,
    setSelectedBaselineId,
    selectedCurrentId,
    setSelectedCurrentId,
    selectedPenultimateId,
    setSelectedPenultimateId,
    ...snapshotObjects
  };
};

/**
 * Custom hook for managing budget data fetching and state
 * @param {Object} initialBudgetData - Initial budget data
 * @returns {Object} - Budget data state and handlers
 */
export const useBudgetData = (initialBudgetData) => {
  const [updatedBudgetData, setUpdatedBudgetData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const dataToUse = updatedBudgetData || initialBudgetData;

  const handleFetchBudgetData = async (fetchFunction, projectId, baselineId, penultimateId, currentId) => {
    if (!baselineId || !penultimateId || !currentId) {
      return;
    }
    setIsLoading(true);
    setIsComplete(false);
    try {
      const data = await fetchFunction(projectId, baselineId, penultimateId, currentId);
      setUpdatedBudgetData(data);
    } catch (error) {
      console.error("Error fetching budget data:", error);
      toast.error(error.message || "An unexpected error occurred.", toastStyle());
    } finally {
      setIsLoading(false);
      setIsComplete(true);
      setTimeout(() => setIsComplete(false), 1500); // Only flashing complete for 1.5 sec
    }
  };

  return {
    dataToUse,
    updatedBudgetData,
    setUpdatedBudgetData,
    isLoading,
    isComplete,
    handleFetchBudgetData
  };
};